<template>
  <v-navigation-drawer
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :permanent="permanent"
    :temporary="!permanent"
    :scrim="!permanent"
    :width="SIDEBAR_WIDTH"
    app
    class="app-sidebar"
  >
    <v-list dense nav class="pt-4">
      <template v-for="item in menuItems" :key="item.title">
        <template v-if="item.children && item.children.length">
          <!-- Clickable parent item -->
          <v-list-item
            :to="item.name ? { name: item.name } : undefined"
            :prepend-icon="item.icon"
            :title="item.title"
            :active="isItemActive(item)"
            link
            class="menu-item parent-item"
          />
          <!-- Non-collapsible children - always shown -->
          <v-list-item
            v-for="child in item.children"
            :key="child.title"
            :to="child.name ? { name: child.name } : undefined"
            :prepend-icon="child.icon"
            :title="child.title"
            :active="isItemActive(child)"
            link
            class="menu-item child-item"
          />
        </template>
        <v-list-item
          v-else
          :to="item.name ? { name: item.name } : undefined"
          :prepend-icon="item.icon"
          :title="item.title"
          :active="isItemActive(item)"
          link
          class="menu-item"
        />
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { useMenuItems, type MenuItem } from '@/composables/useMenuItems';
import { useRoute } from 'vue-router';
import { SIDEBAR_WIDTH } from '@/constants/layout';

defineProps<{
  modelValue: boolean;
  permanent: boolean;
}>();

defineEmits(['update:modelValue']);

const { menuItems } = useMenuItems();
const route = useRoute();

const isItemActive = (item: MenuItem): boolean => {
  // Special case for home, which can be the root path
  if (item.name === 'home' && route.path === '/') {
    return true;
  }
  return item.name === route.name;
};
</script>

<style scoped>
.app-sidebar {
  background: #fafafa;
  border-right: 1px solid #e0e0e0;
}

.menu-item {
  position: relative;
  transition: background-color 0.2s ease-in-out;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: capitalize;
  margin: 0.25rem 0.5rem;
  border-radius: 4px;
}

.menu-item :deep(.v-list-item__content) {
  overflow: visible;
}

.menu-item :deep(.v-list-item-title) {
  white-space: nowrap;
  overflow: visible;
  text-overflow: unset;
}

.child-item {
  padding-left: 1.25rem;
}

/* 1. Default State: Remove default browser outline */
.menu-item:focus {
  outline: none;
}

/* 2. Hover State (Mouse) */
.menu-item:hover {
  background-color: #f0f0f0;
}

/* 3. Focus State (Keyboard & Assistive Tech) */
/* This only appears for keyboard users, NOT after a mouse click. */
.menu-item:focus-visible {
  outline: 2px solid var(--v-primary-base, #1976d2);
  outline-offset: 2px;
  background-color: #f0f0f0; /* Match hover for visual consistency */
}

/* 4. Active State (The current page) */
.app-sidebar .v-list-item--active {
  background-color: #e9ecef; /* A slightly darker, persistent grey */
  color: #212529 !important;
  font-weight: 600;
}

/* Optional: Make active state slightly darker on hover */
.app-sidebar .v-list-item--active:hover {
  background-color: #dee2e6;
}
</style> 
